

import { createContext, useContext, ReactNode } from 'react';
import { useLoadScript, type Libraries } from '@react-google-maps/api';

import { GOOGLE_MAPS_SCRIPT_ID } from '../../../../lib/google-maps/use-google-maps-script';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | undefined>(undefined);

interface GoogleMapsGlobalProviderProps {
  children: ReactNode;
  apiKey: string;
  libraries?: Libraries;
}

export function GoogleMapsGlobalProvider({
  children,
  apiKey,
  libraries = ['places', 'geometry', 'marker'],
}: GoogleMapsGlobalProviderProps) {
  // Align the script `id` with the singleton used by `useGoogleMapsScript`
  // so a single <script> tag is inserted for the whole app.
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey || '',
    libraries,
    id: apiKey ? GOOGLE_MAPS_SCRIPT_ID : 'disabled',
  });

  return (
    <GoogleMapsContext.Provider value={{ isLoaded: apiKey ? isLoaded : false, loadError }}>
      {children}
    </GoogleMapsContext.Provider>
  );
}

export function useGoogleMaps() {
  const context = useContext(GoogleMapsContext);
  if (context === undefined) {
    throw new Error('useGoogleMaps must be used within GoogleMapsGlobalProvider');
  }
  return context;
}

// Hook optionnel qui retourne undefined si le contexte n'est pas disponible
export function useGoogleMapsOptional() {
  const context = useContext(GoogleMapsContext);
  return context; // Retourne undefined si le contexte n'est pas disponible
}

